import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { CobaltThemeProvider } from '@cae/cobalt-react/theme';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { AuthErrorScreen } from '@shared/components/error-pages/auth-error/AuthErrorScreen';
import {
  setMsalInstance as setSharedMsalInstance,
  clearMsalInstanceSingleton,
} from '@shared/helpers/msal-instance/msalInstance';
import {
  detectAuthTypeFromStorage,
  clearMsalStorage,
  MSAL_DOMAINS,
} from '@shared/helpers/msal-instance/msalInstance.utils';
import * as React from 'react';
import {
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AuthType } from '../auth-provider/AuthProvider.types';
import { MsalAuthProviderProps, MsalContextValue } from './MsalProvider.types';
import {
  clearAuthResponseHashFromLocation,
  createMsalInstance,
  loginWithRedirect,
} from './MsalProvider.utils';

const MsalContext = createContext<MsalContextValue | undefined>(undefined);

const MsalAuthProvider = memo(
  ({
    children,
  }: Readonly<MsalAuthProviderProps>): React.ReactElement | null => {
    const [activeAuthType, setActiveAuthType] = useState<AuthType>(
      sessionStorage.getItem('auth-type') === 'b2e' ? 'b2e' : 'b2c'
    );
    const [msalInstance, setMsalInstance] =
      useState<PublicClientApplication | null>(null);
    const [initializationError, setInitializationError] = useState(false);
    const [initializationAttempt, setInitializationAttempt] = useState(0);

    useEffect(() => {
      const init = async (): Promise<void> => {
        localStorage.removeItem('id_token');
        // Detect correct auth type using shared utility (URL-aware)
        // This ensures /train always uses B2C and /instruct always uses B2E
        const correctAuthType = detectAuthTypeFromStorage(
          globalThis.location.pathname
        );

        const instance = await createMsalInstance(correctAuthType);
        setActiveAuthType(correctAuthType);

        setSharedMsalInstance(instance, correctAuthType);

        const result = await instance.handleRedirectPromise();

        localStorage.removeItem('pending-auth-type');

        const accounts = instance.getAllAccounts();
        const account =
          result?.account ?? instance.getActiveAccount() ?? accounts[0];
        if (account) {
          instance.setActiveAccount(account);
          // Sync session storage with detected auth type
          sessionStorage.setItem('auth-type', correctAuthType);
        }

        setMsalInstance(instance);
      };
      void init().catch((error: unknown): void => {
        console.error('MSAL initialization failed:', error);
        clearAuthResponseHashFromLocation();
        setInitializationError(true);
      });
    }, [initializationAttempt]);

    const retryInitialization = useCallback((): void => {
      setInitializationError(false);
      setInitializationAttempt(previousAttempt => previousAttempt + 1);
    }, []);

    const handleAuthType = useCallback(
      async (authType: AuthType): Promise<PublicClientApplication | null> => {
        if (activeAuthType === authType) {
          return msalInstance;
        }

        if (msalInstance) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const msalKeys = Object.keys(localStorage).filter(
              key =>
                key.includes(MSAL_DOMAINS.B2E_PRIMARY) ||
                key.includes(MSAL_DOMAINS.B2E_SECONDARY) ||
                key.includes(MSAL_DOMAINS.B2C) ||
                key.includes(MSAL_DOMAINS.MSAL_PREFIX)
            );
            msalKeys.forEach(key => localStorage.removeItem(key));
            sessionStorage.removeItem('auth-type');
          }
        }

        setActiveAuthType(authType);
        sessionStorage.setItem('auth-type', authType);
        localStorage.setItem('pending-auth-type', authType);

        const instance = await createMsalInstance(authType);
        setMsalInstance(instance);
        setSharedMsalInstance(instance, authType);
        return instance;
      },
      [activeAuthType, msalInstance]
    );

    const login = useCallback(
      async (
        authType: AuthType,
        deepLinkUrl?: string,
        loginHint?: string
      ): Promise<void> => {
        // Always store pending auth type BEFORE redirect - this survives the full page
        // reload that happens after MSAL redirects back from Microsoft login.
        // Without this, detectAuthTypeFromStorage can't determine the correct auth type
        // when the redirect URI (/app-initializer) doesn't match a module-specific path.
        // Note: handleAuthType only sets this when SWITCHING types (early return otherwise).
        localStorage.setItem('pending-auth-type', authType);
        const instance = await handleAuthType(authType);
        await loginWithRedirect(instance, deepLinkUrl, loginHint);
      },
      [handleAuthType]
    );

    const logout = useCallback(async (): Promise<void> => {
      // Clear shared singleton before logout redirect
      clearMsalInstanceSingleton();
      clearMsalStorage();
      await msalInstance?.logoutRedirect();
    }, [msalInstance]);

    const value = useMemo<MsalContextValue>(
      () => ({
        activeAuthType,
        login,
        logout,
      }),
      [activeAuthType, login, logout]
    );

    if (initializationError) {
      return (
        <CobaltThemeProvider initialTheme="light" initialBrand="default">
          <AuthErrorScreen onRetry={retryInitialization} />
        </CobaltThemeProvider>
      );
    }

    if (!msalInstance) {
      return <AppLoader />;
    }

    return (
      <MsalContext value={value}>
        <MsalProvider instance={msalInstance}>{children}</MsalProvider>
      </MsalContext>
    );
  }
);

MsalAuthProvider.displayName = 'MsalAuthProvider';

export { MsalAuthProvider, MsalContext };
